/**
 * Everything on Settings that waits for the one Save, and the order it is sent in.
 *
 * ONE BAR SAVES EVERYTHING NOW, AND THAT REVERSES SEVERAL NOTES THAT ARGUED THE OPPOSITE.
 * The greeting, the instructions, the tool switches, the two category lists, the sentiment
 * cards, the voice and the agent type each saved themselves, and each file said why: a
 * greeting save calls a voice vendor, a category delete rewrites filed calls, and pairing
 * either with "fix a typo in the email address" was never a transaction anybody meant.
 * Illia chose the prototype's single bar anyway (2026-09-26), and the objection is answered
 * rather than ignored: the save is not ONE request but an ordered list of the same requests
 * as before, each still its own transaction on the Worker, and a failure is reported against
 * the part that failed while the rest stay saved. What went is the button per card.
 *
 * DRAFTS SURVIVE SWITCHING PANES, which is what makes one bar honest. A pane is unmounted
 * when another is opened, so its fields cannot be its own state - a half-typed brief would
 * vanish on a click and the bar would say there was nothing to save. They live here, above
 * every pane, keyed by agent so switching agent starts clean.
 *
 * DIRTY IS DERIVED, NOT A FLAG, which is the rule the old form kept for the reason it gave:
 * type a character and delete it, and a flag still claims there is something to save. Each
 * part is compared with what was last saved, with the comparisons the old form used -
 * `sameLocations` for the locations, key order aside, trimmed text for words.
 *
 * A REFETCH REDEFINES ONLY WHAT IS CLEAN. `rebase` takes server truth for a part nobody has
 * touched (a recording that landed, a tool the editor just saved, category keys the Worker
 * minted) and leaves a dirty part's baseline alone - the guarantee the old form bought by
 * seeding with `useState` initialisers and rendering without a `key`.
 *
 * THE ORDER IS THE PART WORTH READING. `planSave` is pure so a test can pin it:
 *   1. the agent type, because the hosted routes answer 409 for a custom agent;
 *   2. ONE `POST /v1/settings/agent` with only the keys that changed - one D1 batch;
 *   3. the voice, after the languages it is chosen from are saved;
 *   4. the greeting, after the name and the voice, so it is recorded once, in the final
 *      voice, with the final name in it;
 *   5. the instructions and 6. each tool switch, only if the agent is hosted after the save.
 * A step whose prerequisite failed is skipped rather than sent, and says why.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from "react";
import type { ReactNode } from "react";
import { ApiError, DAY_KEYS, post, type AgentPage, type CategoryInput, type Location, type SettingsPage, type Vocabulary, type VoiceBlock } from "../../api";
import { labelFor } from "../../format";
import type { SectionId } from "./sections";

/* --- the values ---------------------------------------------------------------------------- */

export const SENTIMENTS = ["positive", "neutral", "negative"] as const;
export type Sentiment = (typeof SENTIMENTS)[number];

/** A category as the draft holds it. `key: null` is one added since the last save. */
export interface CategoryRow {
  key: string | null;
  label: string;
  /** Carried, never edited here: the Worker reads a row sent without one as a deletion. */
  description: string;
}

export interface Merge {
  from: string;
  into: string;
}

export interface Values {
  business: { name: string; timezone: string; description: string; email: string; view: "default" | "simple" };
  hours: { locations: Location[]; recordAudio: boolean };
  languages: { primary: string; additional: string[] };
  greeting: { text: string };
  instructions: { text: string };
  /** Enabled, by tool id. The editor saves everything else about a tool itself. */
  tools: Record<string, boolean>;
  taxonomy: { intents: CategoryRow[]; merges: Merge[] };
  /** An empty string is the platform's wording, which is what the Worker stores it as. */
  sentiments: Record<Sentiment, string>;
  voice: { id: string | null };
  runtime: "custom" | "hosted";
}

export type Part = keyof Values;

export const PARTS: Part[] = [
  "business",
  "hours",
  "languages",
  "greeting",
  "instructions",
  "tools",
  "taxonomy",
  "sentiments",
  "voice",
  "runtime",
];

/** Where each part is edited, for the nav's dots and the bar's "Show" links. */
export const PART_SECTION: Record<Part, SectionId> = {
  business: "details",
  hours: "hours",
  languages: "languages",
  greeting: "greeting",
  instructions: "instructions",
  tools: "tools",
  taxonomy: "tracking",
  sentiments: "tracking",
  voice: "voice",
  runtime: "agenttype",
};

export const PART_LABEL: Record<Part, string> = {
  business: "Business details",
  hours: "Hours & recording",
  languages: "Languages",
  greeting: "Greeting",
  instructions: "Instructions",
  tools: "Tool switches",
  taxonomy: "Call tracking",
  sentiments: "Sentiment",
  voice: "Voice",
  runtime: "Agent type",
};

/** The three the Worker refuses from a member, so a member's draft never counts them. */
const INTERNAL_PARTS: Part[] = ["languages", "voice", "runtime"];

export function rowsOf(words: Vocabulary): CategoryRow[] {
  return words.intents.map((key) => ({
    key,
    label: labelFor(words.labels, key),
    description: words.descriptions?.[key] ?? "",
  }));
}

export function taxonomyOf(words: Vocabulary): Values["taxonomy"] {
  return { intents: rowsOf(words), merges: [] };
}

/** The draft's starting point, read from the two pages the screen fetches. */
export function valuesFrom(settings: SettingsPage, page: AgentPage): Values {
  const agent = settings.agent;
  const words = agent.vocabulary;
  return {
    business: {
      name: agent.name,
      timezone: agent.timezone ?? "",
      description: agent.description ?? "",
      email: agent.notifyEmail ?? "",
      view: agent.view,
    },
    hours: { locations: agent.hours.locations, recordAudio: agent.recordAudio },
    languages: { primary: agent.languages.primary, additional: agent.languages.additional },
    greeting: { text: page.greeting.text ?? "" },
    instructions: { text: page.instructions?.text ?? "" },
    tools: Object.fromEntries((page.tools ?? []).map((tool) => [tool.id, tool.enabled])),
    taxonomy: taxonomyOf(words),
    sentiments: Object.fromEntries(
      SENTIMENTS.map((sentiment) => [sentiment, words.sentiments?.[sentiment]?.trim() ?? ""]),
    ) as Record<Sentiment, string>,
    voice: { id: (agent.voice as VoiceBlock | null)?.id ?? null },
    runtime: page.agent.runtime,
  };
}

/* --- sameness --------------------------------------------------------------------------------- */

/**
 * Are these the same locations?
 *
 * NOT A BARE `JSON.stringify`, AND THE REASON IS A REAL BUG IT CAUSES. The Worker writes its
 * keys in its own order and the dialogs write theirs, and a missing day is the same fact as
 * an empty one. Stringifying as-is calls them different, so a form nobody had touched would
 * come up dirty on load. Each location is laid out in one fixed order first.
 */
export function sameLocations(a: Location[], b: Location[]): boolean {
  const canonical = (l: Location) =>
    JSON.stringify([
      l.id,
      l.name,
      l.address,
      DAY_KEYS.map((day) => l.hours[day] ?? []),
      l.closures.map((c) => [c.id, c.from, c.to, c.note]),
    ]);
  return a.length === b.length && a.every((location, index) => canonical(location) === canonical(b[index]!));
}

export function sameList(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((item, index) => b[index] === item);
}

function sameRows(a: CategoryRow[], b: CategoryRow[]): boolean {
  return a.length === b.length && a.every((row, index) => row.key === b[index]!.key && row.label === b[index]!.label);
}

function sameRecord(a: Record<string, boolean>, b: Record<string, boolean>): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  return [...keys].every((key) => a[key] === b[key]);
}

export function same<P extends Part>(part: P, a: Values[P], b: Values[P]): boolean {
  switch (part) {
    case "business": {
      const [x, y] = [a as Values["business"], b as Values["business"]];
      return x.name === y.name && x.timezone === y.timezone && x.description === y.description && x.email === y.email && x.view === y.view;
    }
    case "hours": {
      const [x, y] = [a as Values["hours"], b as Values["hours"]];
      return sameLocations(x.locations, y.locations) && x.recordAudio === y.recordAudio;
    }
    case "languages": {
      const [x, y] = [a as Values["languages"], b as Values["languages"]];
      return x.primary === y.primary && sameList(x.additional, y.additional);
    }
    case "greeting":
    case "instructions":
      return (a as { text: string }).text.trim() === (b as { text: string }).text.trim();
    case "tools":
      return sameRecord(a as Values["tools"], b as Values["tools"]);
    case "taxonomy": {
      const [x, y] = [a as Values["taxonomy"], b as Values["taxonomy"]];
      return sameRows(x.intents, y.intents) && x.merges.length === y.merges.length;
    }
    case "sentiments": {
      const [x, y] = [a as Values["sentiments"], b as Values["sentiments"]];
      return SENTIMENTS.every((key) => x[key].trim() === y[key].trim());
    }
    case "voice":
      return (a as Values["voice"]).id === (b as Values["voice"]).id;
    case "runtime":
      return a === b;
  }
  return false;
}

/* --- the state ---------------------------------------------------------------------------- */

export interface DraftState {
  baseline: Values;
  draft: Values;
  /** The sentence a failed save left against a part, cleared by the next edit to it. */
  errors: Partial<Record<Part, string>>;
}

export function initialState(values: Values): DraftState {
  return { baseline: values, draft: values, errors: {} };
}

export function dirtyParts(state: DraftState, internal: boolean): Part[] {
  return PARTS.filter(
    (part) => (internal || !INTERNAL_PARTS.includes(part)) && !same(part, state.baseline[part], state.draft[part]),
  );
}

export type Action =
  | { type: "set"; part: Part; value: Values[Part] }
  | { type: "discard" }
  | { type: "rebase"; fresh: Values }
  | { type: "saved"; part: Part; posted: Values[Part]; fresh?: Values[Part] }
  | { type: "savedTool"; id: string; enabled: boolean }
  | { type: "failed"; part: Part; error: string };

export function reducer(state: DraftState, action: Action): DraftState {
  switch (action.type) {
    case "set": {
      const { [action.part]: _cleared, ...errors } = state.errors;
      return { ...state, draft: { ...state.draft, [action.part]: action.value }, errors };
    }
    case "discard":
      return { baseline: state.baseline, draft: state.baseline, errors: {} };
    case "rebase":
      return rebase(state, action.fresh);
    case "saved": {
      const { [action.part]: _cleared, ...errors } = state.errors;
      const baseline = { ...state.baseline, [action.part]: action.fresh ?? action.posted };
      // THE DRAFT FOLLOWS THE SERVER'S VERSION ONLY IF NOBODY TYPED DURING THE SAVE. A
      // brief edited while its own save was in flight is newer than both.
      const untouched = same(action.part, state.draft[action.part], action.posted);
      const draft = untouched && action.fresh ? { ...state.draft, [action.part]: action.fresh } : state.draft;
      return { baseline, draft, errors };
    }
    case "savedTool": {
      const { tools: _cleared, ...errors } = state.errors;
      return { ...state, baseline: { ...state.baseline, tools: { ...state.baseline.tools, [action.id]: action.enabled } }, errors };
    }
    case "failed":
      return { ...state, errors: { ...state.errors, [action.part]: action.error } };
  }
}

/**
 * Server truth for every part nobody has touched.
 *
 * THE TOOL SWITCHES ARE THE ONE PART THAT MERGES. The list itself changes under a pending
 * switch - the editor saves a tool, or creates one - and a switch flicked on one row must
 * not be lost because another row appeared. So the overrides are carried onto the fresh
 * list, and one that now agrees with the server stops being an override.
 */
export function rebase(state: DraftState, fresh: Values): DraftState {
  const baseline = { ...state.baseline };
  const draft = { ...state.draft };
  for (const part of PARTS) {
    if (part === "tools") continue;
    if (same(part, state.baseline[part], state.draft[part])) {
      (baseline as Record<Part, unknown>)[part] = fresh[part];
      (draft as Record<Part, unknown>)[part] = fresh[part];
    }
  }
  const overrides = Object.entries(state.draft.tools).filter(([id, on]) => state.baseline.tools[id] !== on);
  baseline.tools = fresh.tools;
  draft.tools = { ...fresh.tools };
  for (const [id, on] of overrides) if (id in fresh.tools) draft.tools[id] = on;
  return { ...state, baseline, draft };
}

/* --- the categories, as edits to a draft ---------------------------------------------------- */

/**
 * A name added to the list.
 *
 * RE-ADDING A NAME THAT IS WAITING TO BE MERGED AWAY PUTS THE ORIGINAL BACK. Somebody who
 * deletes "Repowers" into Other and types it again a minute later means "undo that", and the
 * Worker would refuse the alternative anyway: a merge's `from` may not also be in the list.
 */
export function addCategory(
  taxonomy: Values["taxonomy"],
  saved: Values["taxonomy"],
  label: string,
): Values["taxonomy"] {
  const pending = taxonomy.merges.find((merge) =>
    saved.intents.some((row) => row.key === merge.from && row.label.trim().toLowerCase() === label.trim().toLowerCase()),
  );
  if (pending) {
    const original = saved.intents.find((row) => row.key === pending.from)!;
    return {
      ...taxonomy,
      intents: [...taxonomy.intents, original],
      merges: taxonomy.merges.filter((merge) => merge !== pending),
    };
  }
  return { ...taxonomy, intents: [...taxonomy.intents, { key: null, label: label.trim(), description: "" }] };
}

export function renameCategory(taxonomy: Values["taxonomy"], index: number, label: string): Values["taxonomy"] {
  return { ...taxonomy, intents: taxonomy.intents.map((row, i) => (i === index ? { ...row, label: label.trim() } : row)) };
}

/**
 * A name taken off the list, and where its calls go.
 *
 * ONLY A SAVED ROW HAS CALLS, so only a saved row records a merge; a name added since the
 * last save simply goes. A merge INTO a row that is itself later merged away is re-pointed
 * to the new target, because the Worker checks every `into` against the list it resolves
 * and a chain it would have to follow is a chain it refuses.
 */
export function removeCategory(
  taxonomy: Values["taxonomy"],
  index: number,
  into: string,
): Values["taxonomy"] {
  const row = taxonomy.intents[index];
  if (!row) return taxonomy;
  const rows = taxonomy.intents.filter((_, i) => i !== index);
  if (!row.key) return { ...taxonomy, intents: rows };
  const from = row.key;
  const merges = taxonomy.merges.map((merge) => (merge.into === from ? { ...merge, into } : merge));
  return { ...taxonomy, intents: rows, merges: [...merges, { from, into }] };
}

function asInput(row: CategoryRow): CategoryInput {
  return { key: row.key, label: row.label, description: row.description || undefined };
}

/* --- the save ------------------------------------------------------------------------------ */

export type StepId = "runtime" | "settings" | "voice" | "greeting" | "instructions" | `tool:${string}`;

export interface Step {
  id: StepId;
  /** The parts this step settles. A tool step settles one switch of the `tools` part. */
  parts: Part[];
  path: string;
  body: Record<string, unknown>;
  /** Skipped, not sent, if any of these failed. */
  needs: StepId[];
}

export interface Plan {
  steps: Step[];
  /** Dirty parts that cannot be sent: instructions or switches on an agent leaving hosted. */
  dropped: Part[];
}

export function planSave(state: DraftState, context: { agentId: string; internal: boolean }): Plan {
  const { baseline, draft } = state;
  const agent = context.agentId;
  const dirty = new Set(dirtyParts(state, context.internal));
  const steps: Step[] = [];
  const dropped: Part[] = [];

  if (dirty.has("runtime")) {
    steps.push({ id: "runtime", parts: ["runtime"], path: "/v1/agent/runtime", body: { agent, runtime: draft.runtime }, needs: [] });
  }

  const body: Record<string, unknown> = { agent };
  const settled: Part[] = [];
  const renamed = dirty.has("business") && draft.business.name !== baseline.business.name;
  if (dirty.has("business")) {
    const [was, now] = [baseline.business, draft.business];
    if (now.name !== was.name) body.name = now.name;
    if (now.timezone !== was.timezone) body.timezone = now.timezone;
    if (now.description !== was.description) body.description = now.description;
    if (now.email !== was.email) body.notifyEmail = now.email;
    if (now.view !== was.view) body.view = now.view;
    settled.push("business");
  }
  if (dirty.has("hours")) {
    if (!sameLocations(draft.hours.locations, baseline.hours.locations)) body.locations = draft.hours.locations;
    if (draft.hours.recordAudio !== baseline.hours.recordAudio) body.recordAudio = draft.hours.recordAudio;
    settled.push("hours");
  }
  if (dirty.has("languages")) {
    body.languages = { primary: draft.languages.primary, additional: draft.languages.additional };
    settled.push("languages");
  }
  if (dirty.has("taxonomy")) {
    const taxonomy: Record<string, unknown> = {
      intents: draft.taxonomy.intents.map(asInput),
    };
    if (draft.taxonomy.merges.length) taxonomy.merges = draft.taxonomy.merges;
    body.taxonomy = taxonomy;
    settled.push("taxonomy");
  }
  if (dirty.has("sentiments")) {
    body.sentiments = Object.fromEntries(
      SENTIMENTS.filter((key) => draft.sentiments[key].trim() !== baseline.sentiments[key].trim()).map((key) => [key, draft.sentiments[key].trim()]),
    );
    settled.push("sentiments");
  }
  if (settled.length) steps.push({ id: "settings", parts: settled, path: "/v1/settings/agent", body, needs: [] });

  // The voice list is the languages' list, so a voice chosen alongside a language change
  // waits for the languages to land.
  if (dirty.has("voice") && draft.voice.id) {
    steps.push({
      id: "voice",
      parts: ["voice"],
      path: "/v1/settings/voice",
      body: { agent, id: draft.voice.id },
      needs: dirty.has("languages") ? ["settings"] : [],
    });
  }

  if (dirty.has("greeting")) {
    const needs: StepId[] = [];
    if (renamed || dirty.has("languages")) needs.push("settings");
    if (steps.some((step) => step.id === "voice")) needs.push("voice");
    steps.push({ id: "greeting", parts: ["greeting"], path: "/v1/agent/greeting", body: { agent, text: draft.greeting.text }, needs });
  }

  const hostedAfter = draft.runtime === "hosted";
  const runtimeNeeds: StepId[] = dirty.has("runtime") ? ["runtime"] : [];
  if (dirty.has("instructions")) {
    if (hostedAfter) {
      steps.push({ id: "instructions", parts: ["instructions"], path: "/v1/agent/instructions", body: { agent, text: draft.instructions.text }, needs: runtimeNeeds });
    } else dropped.push("instructions");
  }
  if (dirty.has("tools")) {
    if (hostedAfter) {
      for (const [id, enabled] of Object.entries(draft.tools)) {
        if (baseline.tools[id] === enabled) continue;
        steps.push({
          id: `tool:${id}`,
          parts: ["tools"],
          path: `/v1/agent/tools/${encodeURIComponent(id)}/enabled`,
          body: { agent, enabled },
          needs: runtimeNeeds,
        });
      }
    } else dropped.push("tools");
  }

  return { steps, dropped };
}

/* --- the provider ---------------------------------------------------------------------------- */

export interface SaveStatus {
  saving: boolean;
  done: number;
  total: number;
  /** Sentences worth saying after a save: a greeting read aloud, a clock recomputing. */
  notices: string[];
  /** When the last save finished with nothing left failed. Drives the transient receipt. */
  savedAt: number | null;
}

interface DraftContextValue {
  state: DraftState;
  dirty: Part[];
  internal: boolean;
  status: SaveStatus;
  set: <P extends Part>(part: P, value: Values[P]) => void;
  discard: () => void;
  save: () => Promise<void>;
}

const DraftContext = createContext<DraftContextValue | null>(null);

export function useDraft(): DraftContextValue {
  const value = useContext(DraftContext);
  if (!value) throw new Error("useDraft outside SettingsDraftProvider");
  return value;
}

/**
 * The draft, the bar's status, and the save that runs the plan.
 *
 * MOUNTED WITH `key={agent.id}` BY THE PAGE, so a different agent is a different draft. A
 * refetch of the same agent goes through `rebase`, never through a remount.
 */
export function SettingsDraftProvider({
  settings,
  page,
  onSaved,
  onAgentChanged,
  children,
}: {
  settings: SettingsPage;
  page: AgentPage;
  /** Refetch both pages. */
  onSaved: () => void;
  /** Refetch the shell's copy of the agent: its name, view, runtime or vocabulary moved. */
  onAgentChanged: () => void;
  children: ReactNode;
}) {
  const internal = settings.user.role === "internal";
  const fresh = useMemo(() => valuesFrom(settings, page), [settings, page]);
  const [state, dispatch] = useReducer(reducer, fresh, initialState);
  const [status, setStatus] = useState<SaveStatus>({ saving: false, done: 0, total: 0, notices: [], savedAt: null });
  // The plan reads the latest state, not the one captured by whichever render bound `save`.
  const latest = useRef(state);
  latest.current = state;
  const first = useRef(true);

  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    dispatch({ type: "rebase", fresh });
  }, [fresh]);

  const dirty = useMemo(() => dirtyParts(state, internal), [state, internal]);

  /*
   * THE BROWSER'S OWN WARNING for a reload or a closed tab. A click in the top bar is not an
   * unload; `leaveGuard` below covers that without converting the app to a data router.
   */
  useEffect(() => {
    if (!dirty.length) return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty.length]);

  const set = useCallback(<P extends Part>(part: P, value: Values[P]) => {
    dispatch({ type: "set", part, value });
    setStatus((current) => (current.savedAt || current.notices.length ? { ...current, savedAt: null, notices: [] } : current));
  }, []);

  const discard = useCallback(() => dispatch({ type: "discard" }), []);

  const save = useCallback(async () => {
    const snapshot = latest.current;
    const agentId = settings.agent.id;
    const { steps, dropped } = planSave(snapshot, { agentId, internal });
    const notices: string[] = [];
    const failed = new Set<StepId>();
    let shellMoved = false;
    setStatus({ saving: true, done: 0, total: steps.length, notices: [], savedAt: null });

    for (const [index, step] of steps.entries()) {
      const blocker = step.needs.find((need) => failed.has(need));
      if (blocker) {
        failed.add(step.id);
        for (const part of step.parts) {
          dispatch({ type: "failed", part, error: `Not saved, because ${blockerName(blocker)} did not save.` });
        }
        continue;
      }
      try {
        const answer = await post<Record<string, unknown>>(step.path, step.body);
        settle(step, snapshot, answer, dispatch, notices);
        if (step.id === "runtime" || step.id === "settings") shellMoved = true;
        if (step.id === "settings" && "timezone" in step.body) notices.push("Recomputing the local time of past calls. The charts update in a few seconds.");
      } catch (err) {
        failed.add(step.id);
        const message = err instanceof ApiError ? err.message : "The dashboard could not reach the server.";
        for (const part of step.parts) dispatch({ type: "failed", part, error: message });
      }
      setStatus((current) => ({ ...current, done: index + 1 }));
    }
    for (const part of dropped) {
      dispatch({ type: "failed", part, error: "Not saved: this agent is custom-built now, so it has no instructions or tools to change." });
    }

    setStatus({ saving: false, done: steps.length, total: steps.length, notices, savedAt: failed.size || dropped.length ? null : Date.now() });
    if (steps.length) onSaved();
    if (shellMoved) onAgentChanged();
  }, [settings.agent, internal, onSaved, onAgentChanged]);

  const value = useMemo(() => ({ state, dirty, internal, status, set, discard, save }), [state, dirty, internal, status, set, discard, save]);
  return <DraftContext.Provider value={value}>{children}</DraftContext.Provider>;
}

function blockerName(id: StepId): string {
  if (id === "runtime") return "the agent type";
  if (id === "settings") return "the business settings";
  return id;
}

/** What a step that went through changes in the draft. */
function settle(
  step: Step,
  snapshot: DraftState,
  answer: Record<string, unknown>,
  dispatch: (action: Action) => void,
  notices: string[],
) {
  const draft = snapshot.draft;
  if (step.id.startsWith("tool:")) {
    dispatch({ type: "savedTool", id: step.id.slice(5), enabled: Boolean(step.body.enabled) });
    return;
  }
  switch (step.id) {
    case "runtime":
      dispatch({ type: "saved", part: "runtime", posted: draft.runtime });
      return;
    case "settings": {
      const words = (answer.agent as { vocabulary?: Vocabulary } | null)?.vocabulary;
      for (const part of step.parts) {
        // THE CATEGORY KEYS ARE THE WORKER'S. A row sent with `key: null` comes back with
        // one, and the merges are spent, so the saved taxonomy is read out of the answer.
        const fresh = part === "taxonomy" && words ? taxonomyOf(words) : undefined;
        dispatch({ type: "saved", part, posted: draft[part], fresh });
      }
      return;
    }
    case "voice": {
      dispatch({ type: "saved", part: "voice", posted: draft.voice });
      const recording = answer.recording as { error?: string } | undefined;
      if (recording?.error) {
        notices.push(`The voice was changed, but your greeting could not be recorded in it (${recording.error}), so it is read aloud until you record it again.`);
      }
      return;
    }
    case "greeting": {
      const greeting = answer.greeting as { text: string | null } | null | undefined;
      dispatch({ type: "saved", part: "greeting", posted: draft.greeting, fresh: { text: greeting?.text ?? "" } });
      if (typeof answer.warning === "string") notices.push(answer.warning);
      return;
    }
    case "instructions": {
      const instructions = answer.instructions as { text: string } | undefined;
      dispatch({ type: "saved", part: "instructions", posted: draft.instructions, fresh: instructions ? { text: instructions.text } : undefined });
      return;
    }
  }
}

/* --- leaving with unsaved changes -------------------------------------------------------------- */

/**
 * The question asked before a click in the top bar throws the draft away.
 *
 * A MODULE-LEVEL SLOT RATHER THAN A CONTEXT, because the thing asking - the agent switcher in
 * `Shell.tsx` - is above the page that owns the draft, and a provider around the whole shell
 * for one boolean would be more machinery than the question. Settings fills the slot while
 * something is unsaved and empties it when nothing is.
 */
let leaveGuard: (() => boolean) | null = null;

export function setLeaveGuard(guard: (() => boolean) | null) {
  leaveGuard = guard;
}

/** True to go ahead. */
export function confirmLeave(): boolean {
  return leaveGuard ? leaveGuard() : true;
}

export const LEAVE_QUESTION = "You have unsaved changes in Settings. Leave without saving them?";
